import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import {
  createEmptyOrganizationOnboardingForm,
  OrganizationOnboardingFormState,
} from '../../types/organization-onboarding.type';
import { OrganizationType } from '../../types/organization.type';
import { submitOrganizationOnboarding } from '../../services/onboarding.service';
import { validateOnboardingStep } from './onboarding.validation';
import { OnboardingStep1Basics } from './steps/OnboardingStep1Basics';
import { OnboardingStep2Compliance } from './steps/OnboardingStep2Compliance';
import { OnboardingStep3Operations } from './steps/OnboardingStep3Operations';
import { OnboardingStep4Policies } from './steps/OnboardingStep4Policies';

const STEP_COUNT = 4;

const ONBOARDING_STEP_KEYS = [
  'onboarding.steps.basics',
  'onboarding.steps.compliance',
  'onboarding.steps.operations',
  'onboarding.steps.policies',
] as const;

function parseSubmitError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { data?: { detail?: unknown } } }).response;
    const d = r?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) {
      const parts = d
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: string }).msg ?? '');
          }
          return '';
        })
        .filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

const OrganizationOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<OrganizationOnboardingFormState>(createEmptyOrganizationOnboardingForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submissionReferenceId, setSubmissionReferenceId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const stepLabels = useMemo(
    () => ONBOARDING_STEP_KEYS.map((key) => t(key)),
    [t]
  );

  const setField = useCallback(
    <K extends keyof OrganizationOnboardingFormState>(key: K, value: OrganizationOnboardingFormState[K]) => {
      setForm((prev) => {
        const next: OrganizationOnboardingFormState = { ...prev, [key]: value };
        if (key === 'organization_type') {
          const orgType = value as OrganizationType | '';
          if (orgType !== OrganizationType.RECYCLER && orgType !== OrganizationType.TRUCK) {
            next.mwan_certificate_document = null;
            next.mwan_license_number = '';
          }
        }
        return next;
      });
      setErrors((prev) => {
        if (!prev[key as string]) return prev;
        const copy = { ...prev };
        delete copy[key as string];
        return copy;
      });
    },
    []
  );

  const handleNext = () => {
    const stepErrors = validateOnboardingStep(activeStep, form);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;
    setSubmitError(null);
    setActiveStep((s) => Math.min(STEP_COUNT - 1, s + 1));
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    const stepErrors = validateOnboardingStep(3, form);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    setSubmissionReferenceId(null);
    try {
      const res = await submitOrganizationOnboarding(form);
      setSubmissionReferenceId(res.submission_id);
      setSubmitSuccess(true);
    } catch (e) {
      setSubmitError(parseSubmitError(e, t('onboarding.genericError')));
    } finally {
      setSubmitting(false);
    }
  };

  const stepContent = () => {
    switch (activeStep) {
      case 0:
        return <OnboardingStep1Basics form={form} errors={errors} setField={setField} />;
      case 1:
        return <OnboardingStep2Compliance form={form} errors={errors} setField={setField} />;
      case 2:
        return <OnboardingStep3Operations form={form} errors={errors} setField={setField} />;
      case 3:
        return <OnboardingStep4Policies form={form} errors={errors} setField={setField} />;
      default:
        return null;
    }
  };

  return (
    <Container component="main" maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ minHeight: '100vh' }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src={
                theme.palette.mode === 'dark'
                  ? new URL('/images/ops_dark.png', import.meta.url).href
                  : new URL('/images/ops_light.png', import.meta.url).href
              }
              alt={t('common.appName')}
              style={{ height: 56 }}
            />
            <Typography component="h1" variant="h5" sx={{ mt: 2, fontWeight: 600 }}>
              {t('onboarding.registerTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('onboarding.registerSubtitle')}
            </Typography>
          </Box>

          {!submitSuccess ? (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {stepLabels.map((label, index) => (
                <Step key={ONBOARDING_STEP_KEYS[index]}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          ) : null}

          {submitSuccess ? (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: { xs: 3, sm: 4 },
                textAlign: 'center',
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
                backgroundImage: (t) =>
                  t.palette.mode === 'dark'
                    ? 'linear-gradient(180deg, rgba(46, 125, 50, 0.12) 0%, transparent 52%)'
                    : 'linear-gradient(180deg, rgba(76, 175, 80, 0.1) 0%, transparent 50%)',
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                  boxShadow: (t) => `0 8px 24px ${t.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.35)' : 'rgba(46, 125, 50, 0.25)'}`,
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 48 }} />
              </Avatar>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {t('onboarding.successTitle')}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="flex-start" sx={{ maxWidth: 420, mx: 'auto', mb: 2 }}>
                <NotificationsActiveOutlinedIcon sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }} fontSize="small" />
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, textAlign: 'left' }}>
                  {t('onboarding.successMessage')}
                </Typography>
              </Stack>
              {submissionReferenceId ? (
                <>
                  <Divider sx={{ my: 2.5 }} />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ color: 'text.secondary', mb: 1 }}
                  >
                    <TagRoundedIcon fontSize="small" />
                    <Typography variant="overline" letterSpacing={0.08} sx={{ lineHeight: 1 }}>
                      {t('onboarding.referenceIdLabel')}
                    </Typography>
                  </Stack>
                  <Typography
                    component="p"
                    variant="body2"
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontWeight: 600,
                      wordBreak: 'break-all',
                      px: 1,
                    }}
                  >
                    {submissionReferenceId}
                  </Typography>
                </>
              ) : null}
            </Paper>
          ) : null}
          {submitError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          ) : null}

          {!submitSuccess ? <Box sx={{ mb: 3 }}>{stepContent()}</Box> : null}

          {!submitSuccess ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              <Button onClick={handleBack} disabled={activeStep === 0 || submitting}>
                {t('onboarding.back')}
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {activeStep < STEP_COUNT - 1 ? (
                  <Button variant="contained" onClick={handleNext} disabled={submitting}>
                    {t('onboarding.next')}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? t('onboarding.submitting') : t('onboarding.submitApplication')}
                  </Button>
                )}
              </Box>
            </Box>
          ) : (
            <Button component={RouterLink} to="/login" variant="contained" fullWidth sx={{ mt: 1 }}>
              {t('onboarding.goToSignIn')}
            </Button>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default OrganizationOnboardingPage;
